import type { SFC } from 'react';
import { Component } from 'react';
import { Button, ListGroup, OverlayTrigger, Panel, Tooltip } from 'react-bootstrap';
import { ConnectDropTarget, DropTarget, DropTargetMonitor } from 'react-dnd';
import { connect as connectStore } from 'react-redux';
import _ from 'underscore';

import type { LicensingRuleType } from '../../../../shared/model-typings';
import { Action } from '../../../../typings/permissions';
import { window } from '../../../globals';
import { userHasPermissionForPolicyType } from '../../../helpers/permissions';
import PolicyModule from '../../../redux/policies';
import { LicensingRule, Policy } from '../../../types';
import { requirePremium, verify_subscription } from '../../../utils';
import Icon, { icons, IconText } from '../../generic/Icon';
import { RuleItem, RuleItemDragProps } from './RuleItem';
import { customizeableLicensePoliciesFeature } from '../../settings/billing/features/consts';

type RuleListProps = {
  policy: Policy;
  title: string;
  ruleType?: LicensingRuleType;
  bsStyle?: string;
  isDefault?: boolean;
  icon?: string;
  children?: Component;
  onRuleAdd?(defaultRule?: LicensingRule): void;
  onRuleEdit?: (ruleId: number) => void;
  onRuleRemove?: (ruleId: number) => void;
  onSetAsDefault?(): void;
};

type DropTargetProps = {
  connectDropTarget: ConnectDropTarget;
  isOver: boolean;
};

const InnerRuleList: SFC<RuleListProps & DropTargetProps> = (props) => {
  const renderedList = props.policy
    ? _.chain(props.policy.rules as LicensingRule[])
        .filter((r) => r.type === props.ruleType)
        .sortBy((r) => r.licenseId.toLowerCase())
        .map((rule) => {
          // `ruleList.length > 0` implies `!!props.policy` since `Array#filter` and `Array#map` are synchronous.
          return (
            <RuleItem
              key={`$rule-${rule.ruleId}`}
              policy={props.policy}
              onEdit={props.onRuleEdit ? requirePremium(() => props.onRuleEdit(rule.ruleId)) : undefined}
              onRemove={props.onRuleRemove ? requirePremium(() => props.onRuleRemove(rule.ruleId)) : undefined}
              rule={rule}
            />
          );
        })
        .value()
    : [];

  const canEdit = userHasPermissionForPolicyType(Action.Edit, props.policy.type);

  const defaultButton = (
    <OverlayTrigger placement="top" overlay={<Tooltip id="makeDefaultTooltip">Make Default</Tooltip>}>
      <Button
        onClick={requirePremium(props.onSetAsDefault)}
        style={{ marginRight: '5px' }}
        className="pull-right policy-col-btn"
        bsSize="xsmall"
      >
        <Icon icon={icons.check} />
      </Button>
    </OverlayTrigger>
  );

  return props.connectDropTarget(
    // connectDropTarget needs a native DOM element, hence the div
    <div>
      <Panel className="panel-inset policy-col-header" bsStyle={props.bsStyle}>
        <div className={props.isDefault ? 'padded padded-small selected-default' : 'padded padded-small'}>
          {canEdit
            ? [
                props.ruleType && (
                  <Button onClick={props.onRuleAdd} className="pull-right policy-col-btn" bsSize="xsmall">
                    <IconText icon={icons.plus} text="New" />
                  </Button>
                ),
                ' ',
                props.isDefault ? (
                  <OverlayTrigger placement="top" overlay={<Tooltip id="defaultActionTooltip">Default Action</Tooltip>}>
                    <span className="pull-right clickable">
                      <span className="small margin-right-xs">Default</span>
                      <span style={{ padding: '3px' }}>
                        <Icon icon={icons.check} />
                      </span>
                    </span>
                  </OverlayTrigger>
                ) : (
                  props.ruleType && defaultButton
                ),
              ]
            : null}
          <h4>
            <span className="badge margin-right-xs">
              {props.children ? props.children.length : renderedList.length}
            </span>
            {props.title}
          </h4>
        </div>
        <ListGroup fill className={`policy-rule-list ${props.isOver ? 'hover' : ''}`}>
          {props.children ||
            (renderedList.length > 0 ? (
              renderedList
            ) : props.isOver ? (
              ''
            ) : (
              <div className="text-center">
                <i>Drag or add a new rule here.</i>
              </div>
            ))}
        </ListGroup>
      </Panel>
    </div>,
  );
};

const mapDispatchToProps = {
  deletePolicyRule: PolicyModule.actions.deletePolicyRule,
  updatePolicyRule: PolicyModule.actions.updatePolicyRule,
};

export const RuleList = connectStore(null, mapDispatchToProps)(DropTarget(
  'rule-item',
  {
    drop(props: RuleListProps, monitor: DropTargetMonitor) {
      if (!verify_subscription('premium')) {
        window.showPlanSelectorModal({ focusFeature: customizeableLicensePoliciesFeature });
      }
      const item = monitor.getItem() as RuleItemDragProps;
      const updatedRule = _.extend({}, item.rule);
      if (updatedRule.type == null) {
        props.onRuleAdd?.(item.rule);
      } else if (updatedRule.type !== props.ruleType) {
        if (props.ruleType == null) {
          props.deletePolicyRule(item.policy.id, item.rule.ruleId);
        } else {
          updatedRule.type = props.ruleType;
          props.updatePolicyRule(item.policy.id, item.rule.ruleId, updatedRule);
        }
      }
    },
  },
  (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
  }),
)(InnerRuleList));
